/**
 * /exclusive · the Maison (luxury goods) pillar surface · Google
 * Places-backed. Sister page to /fit (Fit.jsx) in shape and
 * discipline, tuned for the Maison taxonomy.
 *
 * Founder ruling (Feb 2026 · scope locked in msg 572):
 *   • 15 cities · 6 categories (florists · jewellers · galleries ·
 *     tailors · boutiques · wine).
 *   • Pure brief-only V1 · the only CTA on a Maison card is "Send a
 *     brief". The desk handles every request.
 *   • Level-1 category tiles → Level-2 occasion quick-picks → a
 *     treasure-map view (Google Static Maps) of pin-numbered Maisons.
 *
 * Embed mode: `?embed=widget` hides global chrome via embed.css ·
 * brief modal posts to /api/exclusive/request which goes through the
 * Aurelion dispatcher (tenant=aurelion|mindescapes → MindEscapes Zoho).
 */
import React, { useState, useEffect, useMemo } from "react";
import { useEmbedMember } from "@/hooks/useEmbedMember";
import { TableFooter } from "@/components/table/TableFooter";
import { PlacesAutocomplete } from "@/components/shared/PlacesAutocomplete";
import ConciergeHero from "@/components/global/ConciergeHero";
import ConciergeOnDutyPill from "@/components/global/ConciergeOnDutyPill";
import ConciergeSurfaceMenu from "@/components/global/ConciergeSurfaceMenu";
import CrossSurfaceNav from "@/components/global/CrossSurfaceNav";
import { Link } from "react-router-dom";
import { dualTimeLine } from "@/lib/dual-time";
import { DrawerReviewsBand } from "@/components/shared/DrawerAtmosphere";

import MemberContextBand from "../components/MemberContextBand";
const API = process.env.REACT_APP_BACKEND_URL || "";

// Editorial tokens (synced with /stay · /escape · /enjoy · /fit)
const T = {
  bg:     "#F4EFE6",
  card:   "#FCFAF4",
  ink:    "#211C15",
  ink2:   "#5C5447",
  ink3:   "#8A8071",
  line:   "#E5DCCB",
  line2:  "#D8CDB7",
  brass:  "#8B6914",
};

const CATEGORIES = [
  { code: "florists",  label: "Florists",                short: "Florists",  glyph: "❀" },
  { code: "jewellers", label: "Jewellers & Watchmakers", short: "Jewellers", glyph: "◈" },
  { code: "galleries", label: "Galleries",               short: "Galleries", glyph: "▣" },
  { code: "tailors",   label: "Tailors & Ateliers",      short: "Tailors",   glyph: "✂" },
  { code: "boutiques", label: "Boutiques",               short: "Boutiques", glyph: "♦" },
  { code: "wine",      label: "Wine Merchants",          short: "Wine",      glyph: "❦" },
];

// Level-2 occasion quick-picks · per-category prompt seeds the brief.
const OCCASIONS = {
  florists:  ["Birthday flowers", "Anniversary bouquet", "Funeral wreath", "Event florals"],
  jewellers: ["Engagement ring", "Anniversary gift", "Bespoke commission", "Watch service"],
  galleries: ["First acquisition", "Private viewing", "Gift commission", "Investment piece"],
  tailors:   ["Bespoke suit", "Made-to-measure shirt", "Wedding attire", "Atelier fitting"],
  boutiques: ["Personal shopping", "Gift for a partner", "Wardrobe refresh", "Sample-size hold"],
  wine:      ["Cellar additions", "Birth-year vintage", "Dinner pairing", "Investment case"],
};

export default function MaisonPage() {
  const [q, setQ]               = useState("");
  const [category, setCategory] = useState("boutiques");
  const [results, setResults]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]       = useState(null);
  const [briefVenue, setBriefVenue] = useState(null);  // null | venueCard | {_open_ask:true, occasion?}
  const [openDetail, setOpenDetail] = useState(null);  // drawer · null | venueCard

  // Viewport-aware layout
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 760px)");
    const update = () => setIsNarrow(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  async function runSearch(query, cat) {
    const term = (query ?? q).trim();
    const useCat = cat ?? category;
    if (!term) return;
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const url = new URL(`${API}/api/exclusive/search`);
      url.searchParams.set("q", term);
      url.searchParams.set("category", useCat);
      const r = await fetch(url.toString());
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `search_failed (${r.status})`);
      setResults(data);
    } catch (e) {
      setError(e.message || "Could not load Maisons.");
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (!results?.next_page_token || loadingMore) return;
    setLoadingMore(true);
    try {
      const url = new URL(`${API}/api/exclusive/search`);
      url.searchParams.set("q", results.city_query);
      url.searchParams.set("category", results.category || "boutiques");
      url.searchParams.set("page_token", results.next_page_token);
      const r = await fetch(url.toString());
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `loadmore_failed (${r.status})`);
      setResults((prev) => ({
        ...prev,
        cards: [...(prev.cards || []), ...(data.cards || [])],
        next_page_token: data.next_page_token || null,
      }));
    } catch (e) {
      setError(e.message || "Could not load more Maisons.");
    } finally {
      setLoadingMore(false);
    }
  }

  function pickCategory(code) {
    setCategory(code);
    if (q.trim()) runSearch(q, code);
  }

  return (
    <div
      data-testid="maison-page-root"
      data-table-surface="exclusive"
      style={{
        background: T.bg, minHeight: "100vh", color: T.ink,
        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
        "--t-bg": T.bg, "--t-ink": T.ink, "--t-ink2": T.ink2,
        "--t-ink3": T.ink3, "--t-line": T.line, "--t-brass": T.brass,
      }}
    >
      <MaisonTopBar onHome={() => { setResults(null); setQ(""); }} />
      <MemberContextBand pillar="maison" />

      <main style={{ maxWidth: 1180, margin: "0 auto", padding: "12px 28px 80px" }}>
{/* Hero · category tiles + city autocomplete */}
        <ConciergeHero
          testid="maison-hero"
          eyebrow="Concierge® · Maison"
          headline="What can we"
          headlineTail="source for you?"
          subtitle={<>A flower, a stone, a canvas, a cellar&mdash;the Concierge® arranges it.</>}
          maxWidth={980}
        >
          {/* Level-1 category tile row */}
          <div
            data-testid="maison-category-row"
            style={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 16,
            }}
          >
            {CATEGORIES.map((c) => {
              const on = category === c.code;
              return (
                <button
                  key={c.code}
                  type="button"
                  data-testid={`maison-category-${c.code}`}
                  onClick={() => pickCategory(c.code)}
                  style={{
                    background: on ? T.ink : "transparent",
                    color: on ? T.bg : T.ink2,
                    border: `1px solid ${on ? T.ink : T.line2}`,
                    borderRadius: 999,
                    padding: isNarrow ? "7px 12px" : "8px 16px",
                    fontSize: 12.5, fontWeight: 500,
                    cursor: "pointer", fontFamily: "inherit",
                    transition: "all 0.12s",
                    display: "inline-flex", alignItems: "center", gap: 7,
                  }}
                >
                  <span style={{ color: on ? T.brass : T.brass, fontSize: 13 }}>{c.glyph}</span>
                  {isNarrow ? c.short : c.label}
                </button>
              );
            })}
          </div>

          {/* Search bar · city + go */}
          <form
            data-testid="maison-search-bar"
            onSubmit={(e) => { e.preventDefault(); runSearch(); }}
            style={{
              background: T.card,
              border: `1px solid ${T.line2}`,
              borderRadius: 18,
              padding: isNarrow ? "12px" : "8px 8px 8px 18px",
              display: "grid",
              gridTemplateColumns: isNarrow ? "1fr" : "1fr auto",
              gap: isNarrow ? 10 : 8,
              alignItems: "center",
              maxWidth: 760,
              margin: "0 auto",
              boxShadow: "0 6px 22px rgba(33,28,21,0.04)",
            }}
          >
            <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
              <PlacesAutocomplete
                value={q}
                onChange={setQ}
                onSubmit={(picked) => { setQ(picked); runSearch(picked); }}
                placeholder="City — Paris · Milan · Tokyo · Mumbai…"
                types="(cities)"
                testidPrefix="maison-search"
                inputStyle={{
                  border: 0, background: "transparent", outline: "none",
                  borderRadius: 0, fontSize: 16, color: T.ink,
                  padding: "10px 12px",
                  fontFamily: "inherit",
                }}
              />
            </div>
            <button
              type="submit"
              data-testid="maison-search-submit"
              disabled={loading || !q.trim()}
              style={{
                background: T.ink, color: T.card, border: 0, borderRadius: 14,
                padding: "12px 22px", fontSize: 14, fontWeight: 600,
                cursor: loading ? "wait" : (!q.trim() ? "not-allowed" : "pointer"),
                opacity: (loading || !q.trim()) ? 0.65 : 1,
                fontFamily: "inherit",
                width: isNarrow ? "100%" : "auto",
              }}
            >
              {loading ? "Searching…" : "Find Maisons"}
            </button>
          </form>

          {/* Two CTA row */}
          <div
            data-testid="maison-hero-secondary-row"
            style={{
              display: "flex", justifyContent: "center", alignItems: "center",
              gap: 18, marginTop: 18, flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              data-testid="maison-browse"
              onClick={() => runSearch("Paris")}
              style={{
                background: "transparent",
                color: T.ink,
                border: `1.5px solid ${T.ink}`,
                borderRadius: 999,
                padding: "9px 18px",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Browse Maisons in Paris
            </button>
            <button
              type="button"
              data-testid="maison-hero-ask-concierge"
              onClick={() => setBriefVenue({ _open_ask: true })}
              style={{
                background: "transparent",
                color: T.brass,
                border: `1px solid ${T.brass}`,
                borderRadius: 999,
                padding: "10px 22px",
                fontSize: 13.5,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.brass; e.currentTarget.style.color = T.bg; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.brass; }}
            >
              Ask the Concierge®
            </button>
          </div>
          <p
            style={{
              fontSize: 12.5,
              color: T.ink3,
              fontStyle: "italic",
              marginTop: 14,
              marginBottom: 0,
            }}
          >
            One brief. One Concierge®. We source what&apos;s worth sourcing.
          </p>
        </ConciergeHero>

        {/* Anywhere-on-Earth promise · landing state only · example
            chips replace the paragraph form (founder ruling 30 Jun 2026)
            so the brief seeds are tappable. Each chip pre-fills the
            brief modal with that city + category + occasion. */}
        {!results && !loading && !error && (
          <section
            data-testid="maison-anywhere-band"
            style={{
              maxWidth: 880,
              margin: "32px auto 8px",
              textAlign: "center",
              padding: "28px 24px",
            }}
          >
            <p
              style={{
                fontSize: 10.5, letterSpacing: "0.22em",
                textTransform: "uppercase", fontWeight: 600,
                color: T.brass, fontFamily: "ui-monospace, monospace",
                margin: 0,
              }}
            >
              Anywhere a Maison exists
            </p>
            <h2
              className="t-serif"
              style={{
                fontSize: 26, color: T.ink, fontWeight: 500,
                margin: "10px 0 18px", lineHeight: 1.25,
              }}
            >
              Name your city. Name your need. We&apos;ll find the house.
            </h2>
            <div
              data-testid="maison-example-chips"
              style={{
                display: "flex", flexWrap: "wrap",
                justifyContent: "center", gap: 8,
                marginBottom: 8,
              }}
            >
              {[
                { glyph: "❀", text: "Peonies for an anniversary in Lisbon",  city: "Lisbon",   category: "florists",  occasion: "Peonies for an anniversary" },
                { glyph: "✂", text: "A bespoke shirt in Naples",              city: "Naples",   category: "tailors",   occasion: "A bespoke shirt" },
                { glyph: "❦", text: "A 1982 Bordeaux in Mumbai",              city: "Mumbai",   category: "wine",      occasion: "A 1982 Bordeaux" },
                { glyph: "◈", text: "An emerald commission in Jaipur",        city: "Jaipur",   category: "jewellers", occasion: "An emerald commission" },
                { glyph: "♦", text: "A wardrobe refresh in Milan",            city: "Milan",    category: "boutiques", occasion: "A wardrobe refresh" },
                { glyph: "▣", text: "A first acquisition in Paris",           city: "Paris",    category: "galleries", occasion: "A first acquisition" },
              ].map((c, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`maison-example-chip-${i}`}
                  onClick={() => {
                    // Founder ruling 30 Jun 2026 evening: chips MUST
                    // fill the search bar and run the search, NOT
                    // open the brief modal directly. The member should
                    // see the city's results first; the brief modal
                    // is then one click away from a result they like.
                    setQ(c.city);
                    setCategory(c.category);
                    runSearch(c.city, c.category);
                  }}
                  style={{
                    background: T.card,
                    color: T.ink,
                    border: `1px solid ${T.line2}`,
                    borderRadius: 999,
                    padding: "9px 16px",
                    fontSize: 13, fontWeight: 500,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    display: "inline-flex", alignItems: "center", gap: 8,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.brass; e.currentTarget.style.background = T.bg; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.line2; e.currentTarget.style.background = T.card; }}
                >
                  <span style={{ color: T.brass, fontSize: 14 }}>{c.glyph}</span>
                  {c.text}
                </button>
              ))}
            </div>
            <p
              style={{
                fontSize: 12, color: T.ink3,
                fontStyle: "italic",
                margin: "14px 0 0",
              }}
            >
              We work where you work.
            </p>
          </section>
        )}

        {/* Results band */}
        {error && (
          <p data-testid="maison-error" style={{ textAlign: "center", color: "#9a3535", fontSize: 14 }}>{error}</p>
        )}
        {results && results.cards?.length === 0 && (
          <div data-testid="maison-empty" style={{ textAlign: "center", padding: "48px 20px", color: T.ink2, maxWidth: 620, margin: "0 auto" }}>
            <p style={{ fontSize: 15, marginBottom: 18 }}>
              No Maisons found for <strong style={{ color: T.ink }}>{results.city_query}</strong>
              {results.category_label ? <> in <em>{results.category_label}</em></> : null}.
              The Concierge® will find it by hand.
            </p>
            <button
              type="button"
              data-testid="maison-empty-brief"
              onClick={() => setBriefVenue({ _open_ask: true, hint_city: results.city_query })}
              style={{ background: T.ink, color: T.card, border: 0, borderRadius: 999, padding: "12px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
            >
              Send a brief
            </button>
          </div>
        )}
        {results && results.cards?.length > 0 && (
          <>
            {/* Level-2 occasion quick-picks · seeds the brief */}
            <OccasionQuickPicks
              category={results.category || category}
              onPick={(occasion) => setBriefVenue({
                _open_ask: true,
                hint_city: results.city_query,
                hint_category: results.category || category,
                hint_occasion: occasion,
              })}
              isNarrow={isNarrow}
            />

            {/* Treasure-map view */}
            {results.static_map_url && (
              <TreasureMap
                src={results.static_map_url}
                count={Math.min(results.cards.length, 12)}
                city={results.city_query}
                categoryLabel={results.category_label}
              />
            )}

            {/* Maison card grid */}
            <MaisonGrid cards={results.cards} onOpen={setOpenDetail} onBrief={setBriefVenue} />

            {results.next_page_token && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: 36 }}>
                <button
                  type="button"
                  data-testid="maison-load-more"
                  onClick={loadMore}
                  disabled={loadingMore}
                  style={{
                    background: "transparent", color: T.ink,
                    border: `1.5px solid ${T.ink}`, borderRadius: 999,
                    padding: "12px 28px", fontSize: 13.5, fontWeight: 500,
                    cursor: loadingMore ? "wait" : "pointer",
                    fontFamily: "inherit",
                    opacity: loadingMore ? 0.6 : 1,
                  }}
                >
                  {loadingMore ? "Loading…" : `Load more · ${results.cards.length} shown`}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <TableFooter slug="exclusive" />

      {briefVenue && (
        <MaisonRequestModal
          venue={briefVenue}
          city={results?.city_query || briefVenue.hint_city || ""}
          category={results?.category || briefVenue.hint_category || category}
          onClose={() => setBriefVenue(null)}
        />
      )}

      {openDetail && (
        <MaisonDetailDrawer
          card={openDetail}
          onClose={() => setOpenDetail(null)}
          onRequest={() => { setBriefVenue(openDetail); setOpenDetail(null); }}
        />
      )}
    </div>
  );
}


// ── Top bar · mirrors FitTopBar / StayTopBar wordmark pattern ────
function MaisonTopBar({ onHome }) {
  return (
    <header
      style={{
        background: T.bg,
        borderBottom: `1px solid ${T.line}`,
        padding: "14px 22px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        gap: 10, flexWrap: "nowrap", minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
        <Link
          to="/"
          onClick={(e) => { if (onHome) { e.preventDefault(); onHome(); } }}
          data-testid="maison-topbar-home"
          style={{
            textDecoration: "none", color: T.ink,
            display: "inline-flex", alignItems: "baseline", gap: 10, minWidth: 0,
          }}
        >
          <span className="t-serif" style={{ fontSize: 22, fontWeight: 500, letterSpacing: "0.02em" }}>
            /exclusive
          </span>
          <span
            data-testid="maison-header-by-concierge"
            className="lc-mobile-hide-on-narrow"
            style={{
              fontFamily: "ui-monospace, monospace",
              fontSize: 11,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: T.ink3,
              whiteSpace: "nowrap",
            }}
          >
            by Concierge®
          </span>
        </Link>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <ConciergeOnDutyPill context="exclusive" />
        <CrossSurfaceNav testidPrefix="maison-surface-nav" size="compact" />
        <ConciergeSurfaceMenu testid="maison-surface-menu" />
      </div>
    </header>
  );
}


// ── Occasion quick-picks (Level 2) ───────────────────────────────
function OccasionQuickPicks({ category, onPick, isNarrow }) {
  const occasions = OCCASIONS[category] || [];
  if (!occasions.length) return null;
  return (
    <div
      data-testid="maison-occasion-row"
      style={{
        margin: "24px 0 18px",
        display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8,
      }}
    >
      <span
        style={{
          fontSize: 11, color: T.ink3, letterSpacing: "0.18em",
          textTransform: "uppercase", fontWeight: 600,
          fontFamily: "ui-monospace, monospace",
          padding: "8px 4px",
        }}
      >
        Brief seeds ·
      </span>
      {occasions.map((occ) => (
        <button
          key={occ}
          type="button"
          data-testid={`maison-occasion-${occ.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          onClick={() => onPick(occ)}
          style={{
            background: "transparent",
            color: T.brass,
            border: `1px solid ${T.brass}55`,
            borderRadius: 999,
            padding: isNarrow ? "6px 12px" : "7px 16px",
            fontSize: 12.5, fontWeight: 500,
            cursor: "pointer", fontFamily: "inherit",
            transition: "all 0.12s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = T.brass; e.currentTarget.style.color = T.bg; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.brass; }}
        >
          {occ}
        </button>
      ))}
    </div>
  );
}


// ── Treasure map · static Google map with numbered brass pins ────
function TreasureMap({ src, count, city, categoryLabel }) {
  return (
    <section
      data-testid="maison-treasure-map"
      style={{
        marginTop: 8,
        marginBottom: 28,
        background: T.card,
        border: `1px solid ${T.line}`,
        borderRadius: 18,
        overflow: "hidden",
        boxShadow: "0 6px 22px rgba(33,28,21,0.05)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 18px",
          borderBottom: `1px solid ${T.line}`,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <p
            style={{
              fontSize: 10.5, letterSpacing: "0.22em",
              textTransform: "uppercase", fontWeight: 600,
              color: T.brass, fontFamily: "ui-monospace, monospace",
              margin: 0,
            }}
          >
            Treasure map
          </p>
          <p
            className="t-serif"
            style={{
              fontSize: 15, color: T.ink, fontWeight: 500,
              margin: "2px 0 0",
            }}
          >
            {count} {categoryLabel || "Maisons"} in {city}
          </p>
        </div>
        <p style={{ fontSize: 11.5, color: T.ink3, margin: 0, fontStyle: "italic" }}>
          Tap a card below to see it up close.
        </p>
      </div>
      <img
        src={src}
        alt={`Map of ${count} Maisons in ${city}`}
        loading="lazy"
        style={{
          display: "block",
          width: "100%",
          height: "auto",
          maxHeight: 420,
          objectFit: "cover",
          background: T.line,
        }}
      />
    </section>
  );
}


// ── Maison grid · brief-only cards ───────────────────────────────
function MaisonGrid({ cards, onOpen, onBrief }) {
  return (
    <div
      data-testid="maison-grid"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
        gap: 18,
      }}
    >
      {cards.map((c, i) => (
        <MaisonCard
          key={c.place_id || c.name + i}
          venue={c}
          pin={i < 12 ? i + 1 : null}
          onOpen={() => onOpen(c)}
          onBrief={() => onBrief(c)}
        />
      ))}
    </div>
  );
}


function MaisonCard({ venue, pin, onOpen, onBrief }) {
  const hasPhoto = !!venue.photo_url;
  const rating = venue.rating ? Number(venue.rating) : null;
  const ratingCount = venue.user_ratings_total;
  return (
    <article
      data-testid="maison-card"
      style={{
        background: T.card, border: `1px solid ${T.line}`, borderRadius: 16,
        display: "flex", flexDirection: "column",
        overflow: "hidden",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(33,28,21,0.10)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)";  e.currentTarget.style.boxShadow = "none"; }}
    >
      <div
        data-testid="maison-card-photo"
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
        style={{
          aspectRatio: "4 / 3",
          background: hasPhoto
            ? `linear-gradient(180deg, rgba(33,28,21,0) 55%, rgba(33,28,21,0.18) 100%), url(${venue.photo_url}) center/cover no-repeat`
            : `linear-gradient(135deg, ${T.line} 0%, ${T.line2} 60%, ${T.brass}33 100%)`,
          position: "relative",
          cursor: "pointer",
        }}
      >
        {!hasPhoto && (
          <div style={{
            position: "absolute", inset: 0, display: "flex",
            alignItems: "center", justifyContent: "center",
            fontFamily: "'Fraunces', 'EB Garamond', serif",
            fontSize: 30, fontWeight: 500, color: T.brass,
            letterSpacing: "-0.01em",
          }}>
            {(venue.name || "M").trim().charAt(0).toUpperCase()}
          </div>
        )}
        {pin != null && (
          <span
            data-testid="maison-card-pin"
            style={{
              position: "absolute", top: 12, left: 12,
              background: T.brass, color: T.bg,
              width: 24, height: 24, borderRadius: 999,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "ui-monospace, monospace",
              fontSize: 11, letterSpacing: "0", fontWeight: 700,
              boxShadow: "0 2px 6px rgba(33,28,21,0.25)",
            }}
          >
            {pin}
          </span>
        )}
        {venue.primary_type_label && (
          <span
            data-testid="maison-card-type-pill"
            style={{
              position: "absolute", top: 12, right: 12,
              background: "rgba(252,250,244,0.92)", color: T.brass,
              padding: "3px 9px", borderRadius: 999,
              fontFamily: "ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.14em",
              fontWeight: 600, textTransform: "uppercase",
              border: `1px solid ${T.brass}44`,
            }}
          >
            {venue.primary_type_label}
          </span>
        )}
      </div>
      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
        <h3 className="t-serif" style={{ fontSize: 17, fontWeight: 500, lineHeight: 1.22, margin: 0, color: T.ink }}>
          {venue.name || "Untitled Maison"}
        </h3>
        <p style={{ fontSize: 11.5, color: T.ink3, margin: 0, lineHeight: 1.4 }}>
          {venue.short_address || venue.address_line || venue.city_name || "—"}
        </p>
        {rating && (
          <p
            data-testid="maison-card-rating"
            style={{ fontSize: 12, color: T.ink2, margin: 0, display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}
          >
            <span style={{ color: T.brass, fontWeight: 700 }}>★</span>
            <span style={{ fontWeight: 600, color: T.ink }}>{rating.toFixed(1)}</span>
            {ratingCount ? (
              <span style={{ color: T.ink3 }}>
                ({ratingCount.toLocaleString()})
              </span>
            ) : null}
          </p>
        )}
        <div style={{ marginTop: "auto", display: "flex", gap: 8, paddingTop: 12 }}>
          <button
            type="button"
            data-testid="maison-card-view"
            onClick={onOpen}
            style={{
              background: "transparent", color: T.ink2,
              border: `1px solid ${T.line2}`, borderRadius: 999,
              padding: "8px 14px", fontSize: 12, fontWeight: 500,
              cursor: "pointer", fontFamily: "inherit", flex: 1,
            }}
          >
            View
          </button>
          <button
            type="button"
            data-testid="maison-card-brief"
            onClick={onBrief}
            style={{
              background: T.ink, color: T.bg,
              border: 0, borderRadius: 999,
              padding: "8px 14px", fontSize: 12, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit", flex: 1.5,
            }}
          >
            Send a brief
          </button>
        </div>
      </div>
    </article>
  );
}


// ── Detail drawer · enriched · Concierge® voice + reviews + neighbours + map
function MaisonDetailDrawer({ card, onClose, onRequest }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!card?.place_id) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const url = new URL(`${API}/api/exclusive/place/${card.place_id}`);
        if (card.category) url.searchParams.set("category", card.category);
        const r = await fetch(url.toString());
        const data = await r.json();
        if (!cancelled && r.ok) setDetail(data.venue);
      } catch (_e) {
        // Silent fallback to the card data already in hand.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [card]);

  const v = detail || card;
  const photos = (v?.photos && v.photos.length ? v.photos : (v?.photo_url ? [v.photo_url] : []));
  const rating = v?.rating ? Number(v.rating) : null;
  const ratingCount = v?.user_ratings_total;
  const priceTier = _priceTier(v?.price_level);
  const openNow = v?.open_now;
  const todayLine = _todayHoursLine(v?.weekday_hours);
  const neighbours = v?.neighbours || {};
  const voiceLine = v?.concierge_voice?.voice_line;
  const memberSignal = v?.member_whisper;
  // Friendly first-word for the CTA · skip stop-words like "Les"/"Le"/"La"/"The"
  const friendlyName = _friendlyMaisonName(v?.name);
  // Single-pin static map · powered by Google Static Maps via the
  // backend helper if present, otherwise we synthesise the URL inline
  // for the standalone Maison location.
  const miniMapUrl = (v?.lat && v?.lng)
    ? `https://maps.googleapis.com/maps/api/staticmap?size=640x220&scale=2&maptype=roadmap&markers=color:0x8B6914%7C${v.lat},${v.lng}&zoom=16&style=feature:poi%7Cvisibility:simplified&key=`
    : null;
  // The key is appended at runtime via the result band's static_map_url
  // — here we lean on the public CDN-cached version through the same
  // origin. If the key isn't appended client-side we fall back to no
  // map (no broken image).

  return (
    <div
      data-testid="maison-detail-drawer"
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, zIndex: 80,
        background: "rgba(15,12,8,0.5)",
        display: "flex", justifyContent: "flex-end",
      }}
      onClick={onClose}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.bg, width: "min(580px, 100%)",
          height: "100%",
          borderLeft: `1px solid ${T.line}`,
          position: "relative",
          // Flex column · scroll region grows · CTA footer stays pinned
          display: "flex", flexDirection: "column",
        }}
      >
        {/* Close · anchored to the drawer · always visible above scroll */}
        <button
          type="button"
          data-testid="maison-detail-close"
          onClick={onClose}
          style={{
            background: "rgba(244,239,230,0.85)", border: `1px solid ${T.line}`,
            color: T.ink2, fontSize: 18, cursor: "pointer",
            position: "absolute", top: 14, right: 16, zIndex: 5,
            width: 32, height: 32, borderRadius: 999,
            display: "flex", alignItems: "center", justifyContent: "center",
            lineHeight: 1, padding: 0,
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          }}
          aria-label="Close"
        >×</button>

        {/* Scroll region · all drawer content lives here */}
        <div
          data-testid="maison-detail-scroll"
          style={{
            flex: 1, minHeight: 0, overflowY: "auto",
            padding: "24px 28px 28px",
          }}
        >
        {/* Eyebrow · category + neighborhood */}
        <p style={{
          fontSize: 10.5, letterSpacing: "0.22em", textTransform: "uppercase",
          fontWeight: 600, color: T.brass, fontFamily: "ui-monospace, monospace",
          margin: "8px 0 4px",
        }}>
          {v?.primary_type_label || v?.category || "Maison"}
          {v?.neighborhood ? <> · {v.neighborhood}</> : null}
        </p>

        {/* Headline · house name */}
        <h2 className="t-serif" style={{ fontSize: 28, color: T.ink, fontWeight: 500, margin: "0 0 8px", lineHeight: 1.16 }}>
          {v?.name || "Untitled Maison"}
        </h2>

        {/* Stats row · rating · price · open-now */}
        <div
          data-testid="maison-detail-stats"
          style={{
            display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
            marginBottom: 14,
          }}
        >
          {rating && (
            <span style={{ fontSize: 13.5, color: T.ink2, display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ color: T.brass, fontWeight: 700 }}>★</span>
              <strong style={{ color: T.ink }}>{rating.toFixed(1)}</strong>
              {ratingCount ? <span style={{ color: T.ink3 }}>· {ratingCount.toLocaleString()} reviews</span> : null}
            </span>
          )}
          {priceTier && (
            <span style={{
              fontFamily: "ui-monospace, monospace", fontSize: 12,
              color: T.brass, fontWeight: 600, letterSpacing: "0.12em",
            }}>{priceTier}</span>
          )}
          {openNow != null && (
            <span
              data-testid="maison-detail-open-status"
              style={{
                fontSize: 11.5, fontWeight: 600,
                color: openNow ? "#2f8a52" : "#9a3535",
                fontFamily: "ui-monospace, monospace",
                letterSpacing: "0.10em", textTransform: "uppercase",
              }}
            >
              {openNow ? "Open now" : "Closed now"}
              {todayLine ? <span style={{ color: T.ink3, fontWeight: 500, marginLeft: 6 }}>· {todayLine}</span> : null}
            </span>
          )}
          {/* G5 · cross-timezone awareness · only renders when member's
              zone differs from the venue's */}
          {(() => {
            const dual = dualTimeLine(v?.time_zone);
            if (!dual) return null;
            return (
              <span
                data-testid="maison-detail-dual-time"
                style={{
                  fontSize: 11, color: T.ink3,
                  fontFamily: "ui-monospace, monospace",
                  letterSpacing: "0.04em",
                }}
              >
                {dual}
              </span>
            );
          })()}
        </div>

        {/* Address · subtle */}
        <p style={{ fontSize: 12.5, color: T.ink3, margin: "0 0 18px", lineHeight: 1.45 }}>
          {v?.address_line || v?.short_address || v?.city_name || "—"}
          {v?.country_name ? <> · {v.country_name}</> : null}
        </p>

        {/* Concierge® voice line · the atmosphere lift */}
        {voiceLine && (
          <blockquote
            data-testid="maison-detail-voice"
            style={{
              borderLeft: `2px solid ${T.brass}`,
              padding: "8px 0 8px 16px",
              margin: "0 0 16px",
              fontStyle: "italic", color: T.ink,
              fontSize: 15, lineHeight: 1.55,
              fontFamily: "'Fraunces', 'EB Garamond', serif",
            }}
          >
            {voiceLine}
            <footer style={{
              marginTop: 6, fontSize: 10.5, color: T.brass,
              fontFamily: "ui-monospace, monospace", fontStyle: "normal",
              letterSpacing: "0.18em", textTransform: "uppercase",
              fontWeight: 600,
            }}>
              — the Concierge®
            </footer>
          </blockquote>
        )}

        {/* Photo gallery */}
        {photos.length > 0 && (
          <div
            data-testid="maison-detail-gallery"
            style={{
              display: "grid",
              gridTemplateColumns: photos.length === 1 ? "1fr" : "repeat(2, 1fr)",
              gap: 8,
              marginBottom: 18,
            }}
          >
            {photos.slice(0, 4).map((src, i) => (
              <div
                key={i}
                style={{
                  aspectRatio: "4 / 3",
                  background: `url(${src}) center/cover no-repeat`,
                  borderRadius: 10,
                  border: `1px solid ${T.line}`,
                }}
              />
            ))}
          </div>
        )}

        {/* Editorial summary · Google's own one-liner */}
        {v?.editorial_summary && (
          <p
            data-testid="maison-detail-editorial"
            style={{ fontSize: 14, color: T.ink, margin: "0 0 18px", lineHeight: 1.6 }}
          >
            {v.editorial_summary}
          </p>
        )}

        {/* Member whisper · only when ≥3 briefs */}
        {memberSignal && (
          <p
            data-testid="maison-detail-member-whisper"
            style={{
              fontSize: 12, color: T.brass, fontStyle: "italic",
              borderTop: `1px solid ${T.line}`, paddingTop: 10,
              margin: "0 0 14px", lineHeight: 1.5,
            }}
          >
            {memberSignal}
          </p>
        )}

        {/* Cross-pillar whispers · the Concierge® knows the neighbourhood */}
        {(neighbours.hotel || neighbours.restaurant) && (
          <section
            data-testid="maison-detail-neighbours"
            style={{
              borderTop: `1px solid ${T.line}`,
              padding: "14px 0 4px", marginBottom: 8,
            }}
          >
            <p style={{
              fontSize: 11, color: T.ink3, letterSpacing: "0.16em",
              textTransform: "uppercase", fontWeight: 600,
              margin: "0 0 10px", fontFamily: "ui-monospace, monospace",
            }}>
              Around the corner
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {neighbours.hotel && (
                <p style={{ fontSize: 13.5, color: T.ink, margin: 0, lineHeight: 1.5 }}>
                  <span style={{ color: T.brass, marginRight: 6 }}>◆</span>
                  <strong style={{ fontWeight: 600 }}>{neighbours.hotel.walk_min} min walk</strong>
                  {" "}to <em style={{ fontStyle: "normal", color: T.ink2 }}>{neighbours.hotel.name}</em>
                </p>
              )}
              {neighbours.restaurant && (
                <p style={{ fontSize: 13.5, color: T.ink, margin: 0, lineHeight: 1.5 }}>
                  <span style={{ color: T.brass, marginRight: 6 }}>◆</span>
                  <strong style={{ fontWeight: 600 }}>{neighbours.restaurant.walk_min} min walk</strong>
                  {" "}to <em style={{ fontStyle: "normal", color: T.ink2 }}>{neighbours.restaurant.name}</em>
                </p>
              )}
            </div>
          </section>
        )}

        {/* Reviews · trusted voices · shared <DrawerReviewsBand>
           (Parity Sprint Phase 2, 30 Jun 2026 — was Maison-local). */}
        <DrawerReviewsBand reviews={v?.reviews} limit={2} T={T} testid="maison-detail-reviews" />

        {/* Hours · full week */}
        {v?.weekday_hours?.length > 0 && (
          <section
            data-testid="maison-detail-hours"
            style={{
              borderTop: `1px solid ${T.line}`,
              padding: "14px 0 4px", marginBottom: 8,
            }}
          >
            <p style={{
              fontSize: 11, color: T.ink3, letterSpacing: "0.16em",
              textTransform: "uppercase", fontWeight: 600,
              margin: "0 0 8px", fontFamily: "ui-monospace, monospace",
            }}>
              Hours
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 13, color: T.ink2, lineHeight: 1.7 }}>
              {v.weekday_hours.slice(0, 7).map((h) => <li key={h}>{h}</li>)}
            </ul>
          </section>
        )}

        {/* Direct-line row · phone · website · directions */}
        {(v?.phone || v?.website || v?.directions_url) && (
          <section
            data-testid="maison-detail-actions"
            style={{
              borderTop: `1px solid ${T.line}`,
              padding: "14px 0 4px", marginBottom: 8,
              display: "flex", gap: 8, flexWrap: "wrap",
            }}
          >
            {v.phone && (
              <a
                href={`tel:${v.phone}`}
                data-testid="maison-detail-phone"
                style={DETAIL_LINK_STYLE}
              >☏ Call</a>
            )}
            {v.website && (
              <a
                href={v.website}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="maison-detail-website"
                style={DETAIL_LINK_STYLE}
              >↗ House site</a>
            )}
            {v.directions_url && (
              <a
                href={v.directions_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="maison-detail-directions"
                style={DETAIL_LINK_STYLE}
              >➤ Directions</a>
            )}
          </section>
        )}

        {loading && (
          <p style={{ fontSize: 12, color: T.ink3, fontStyle: "italic" }}>Loading detail…</p>
        )}
        </div>

        {/* Pinned CTA footer · sibling of the scroll region, always
            visible at the bottom of the drawer regardless of content
            length. */}
        <div
          data-testid="maison-detail-footer"
          style={{
            background: T.bg,
            borderTop: `1px solid ${T.line}`,
            padding: "14px 28px 18px",
            flexShrink: 0,
            boxShadow: "0 -6px 18px rgba(33,28,21,0.06)",
          }}
        >
          <button
            type="button"
            data-testid="maison-detail-brief"
            onClick={onRequest}
            style={{
              background: T.ink, color: T.bg, border: 0,
              borderRadius: 999, padding: "13px 22px",
              fontSize: 14, fontWeight: 600, cursor: "pointer",
              width: "100%", fontFamily: "inherit",
            }}
          >
            Send a brief about {friendlyName}
          </button>
          <p style={{ fontSize: 11.5, color: T.ink3, textAlign: "center", margin: "8px 0 0", fontStyle: "italic" }}>
            One brief. One Concierge®. The Concierge® arranges the rest.
          </p>
        </div>
      </aside>
    </div>
  );
}

// Helpers used by the drawer.
function _priceTier(level) {
  if (!level) return null;
  const map = {
    PRICE_LEVEL_INEXPENSIVE: "$", PRICE_LEVEL_MODERATE: "$$",
    PRICE_LEVEL_EXPENSIVE: "$$$", PRICE_LEVEL_VERY_EXPENSIVE: "$$$$",
  };
  return map[level] || null;
}

function _todayHoursLine(weekdayHours) {
  if (!weekdayHours?.length) return null;
  try {
    // Google's array is Monday-first. Map JS Sunday=0 to that order.
    const jsDow = new Date().getDay();          // 0=Sun, 1=Mon…
    const mondayFirst = (jsDow + 6) % 7;        // 0=Mon, 6=Sun
    const today = weekdayHours[mondayFirst];
    if (!today) return null;
    // "Monday: 10:30 AM – 8:00 PM" → strip the day prefix
    const m = today.match(/^[A-Za-z]+:\s*(.+)$/);
    return m ? m[1] : today;
  } catch (_e) {
    return null;
  }
}

function _friendlyMaisonName(raw) {
  if (!raw) return "this Maison";
  // Strip definite articles so "Send a brief about Les Lalandes" becomes
  // "Send a brief about Les Lalandes" (full), not "Send a brief about Les".
  // We keep the full name when it's short, take the first two words
  // when it's long.
  const trimmed = raw.trim();
  if (trimmed.length <= 28) return trimmed;
  const words = trimmed.split(/\s+/);
  return words.slice(0, 3).join(" ");
}

const DETAIL_LINK_STYLE = {
  background: "transparent",
  color: T.ink,
  border: `1px solid ${T.line2}`,
  borderRadius: 999,
  padding: "8px 14px",
  fontSize: 12.5, fontWeight: 500,
  textDecoration: "none",
  fontFamily: "inherit",
};


// ── Brief modal · mirrors FitRequestModal but Maison-shaped ──────
function MaisonRequestModal({ venue, city, category, onClose }) {
  const isOpenAsk = !!venue?._open_ask;
  const initialVenueName = isOpenAsk ? "" : (venue.name || "");
  const initialCity = isOpenAsk
    ? (venue.hint_city || city || "")
    : (venue.city_name || city || "");
  const initialOccasion = isOpenAsk ? (venue.hint_occasion || "") : "";

  const embedMember = useEmbedMember();
  const [venueName, setVenueName] = useState(initialVenueName);
  const [cityVal, setCityVal]     = useState(initialCity);
  const [name, setName]           = useState(embedMember?.name  || "");
  const [email, setEmail]         = useState(embedMember?.email || "");
  const [phone, setPhone]         = useState(embedMember?.phone || "");
  const [occasion, setOccasion]   = useState(initialOccasion);
  const [budget, setBudget]       = useState("");
  const [when, setWhen]           = useState("");
  const [preferences, setPreferences] = useState("");
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState(null);
  const [done, setDone]           = useState(null);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (isOpenAsk && !venueName.trim() && !cityVal.trim() && !occasion.trim()) {
      setError("Tell us a city, a Maison, or what you're after.");
      return;
    }
    if (!name.trim() || (!email.trim() && !phone.trim())) {
      setError("Your name plus email or phone — so the Concierge® can reply.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/exclusive/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          venue_name:   venueName || undefined,
          venue_id:     venue?.place_id || undefined,
          city:         cityVal || undefined,
          category:     category || undefined,
          occasion:     occasion || undefined,
          budget:       budget || undefined,
          when:         when || undefined,
          preferences:  preferences || undefined,
          member_name:  name,
          member_email: email || undefined,
          member_phone: phone || undefined,
        }),
      });
      const data = await r.json();
      if (!r.ok || !data?.ok) throw new Error(data?.detail || "Could not send the brief.");
      setDone(data);
    } catch (e) {
      setError(e.message || "Could not send the brief.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="maison-request-modal"
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, zIndex: 90,
        background: "rgba(15,12,8,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.bg, width: "min(560px, 100%)",
          maxHeight: "90vh", overflowY: "auto",
          borderRadius: 18, border: `1px solid ${T.line}`,
          padding: "28px 28px 24px",
          boxShadow: "0 24px 60px rgba(15,12,8,0.35)",
          position: "relative",
        }}
      >
        <button
          type="button"
          data-testid="maison-request-close"
          onClick={onClose}
          style={{
            background: "transparent", border: 0,
            color: T.ink2, fontSize: 22, cursor: "pointer",
            position: "absolute", top: 14, right: 18,
          }}
        >×</button>

        {done ? (
          <div data-testid="maison-request-confirmation" style={{ textAlign: "center", padding: "12px 4px" }}>
            <p style={{
              fontSize: 11, letterSpacing: "0.22em", color: T.brass,
              textTransform: "uppercase", fontWeight: 600,
              fontFamily: "ui-monospace, monospace", margin: 0,
            }}>
              Brief received
            </p>
            <h3 className="t-serif" style={{ fontSize: 24, color: T.ink, fontWeight: 500, margin: "10px 0 8px" }}>
              Thank you · we have it.
            </h3>
            <p style={{ fontSize: 14, color: T.ink2, margin: "0 0 10px" }}>
              Your reference is <strong style={{ color: T.brass, fontFamily: "ui-monospace, monospace" }}>{done.member_reference || done.display_ref}</strong>.
            </p>
            <p style={{ fontSize: 13, color: T.ink3, margin: "0 0 22px", lineHeight: 1.6 }}>
              The Concierge® will reply within the hour with options or a price.
            </p>
            <button
              type="button"
              data-testid="maison-request-done"
              onClick={onClose}
              style={{
                background: T.ink, color: T.bg, border: 0,
                borderRadius: 999, padding: "12px 28px",
                fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
              }}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <p style={{
              fontSize: 10.5, letterSpacing: "0.22em", color: T.brass,
              textTransform: "uppercase", fontWeight: 600,
              fontFamily: "ui-monospace, monospace", margin: 0,
            }}>
              Maison · brief
            </p>
            <h3 className="t-serif" style={{ fontSize: 24, color: T.ink, fontWeight: 500, margin: "8px 0 16px" }}>
              {isOpenAsk
                ? "Tell us what you're sourcing."
                : `A brief about ${venue?.name || "this Maison"}.`}
            </h3>

            {isOpenAsk && (
              <Field label="Maison (optional)" testid="maison-input-venue">
                <input
                  type="text"
                  value={venueName}
                  onChange={(e) => setVenueName(e.target.value)}
                  placeholder="If you have a house in mind…"
                  style={INPUT_STYLE}
                />
              </Field>
            )}

            <Field label="City" testid="maison-input-city">
              <PlacesAutocomplete
                value={cityVal}
                onChange={setCityVal}
                onSubmit={(picked) => setCityVal(picked)}
                placeholder="City — Paris · Milan · Tokyo…"
                types="(cities)"
                testidPrefix="maison-modal-city"
                inputStyle={INPUT_STYLE}
              />
            </Field>

            <Field label="Occasion / what you need" testid="maison-input-occasion">
              <input
                type="text"
                value={occasion}
                onChange={(e) => setOccasion(e.target.value)}
                placeholder="Anniversary bouquet · Engagement ring · Bespoke suit…"
                style={INPUT_STYLE}
              />
            </Field>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Budget (optional)" testid="maison-input-budget">
                <input
                  type="text"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  placeholder="Indicative range"
                  style={INPUT_STYLE}
                />
              </Field>
              <Field label="By when (optional)" testid="maison-input-when">
                <input
                  type="text"
                  value={when}
                  onChange={(e) => setWhen(e.target.value)}
                  placeholder="Next Friday · This month"
                  style={INPUT_STYLE}
                />
              </Field>
            </div>

            <Field label="Anything else" testid="maison-input-preferences">
              <textarea
                value={preferences}
                onChange={(e) => setPreferences(e.target.value)}
                rows={3}
                placeholder="Style notes · sizing · recipient · delivery details…"
                style={{ ...INPUT_STYLE, resize: "vertical", minHeight: 80 }}
              />
            </Field>

            <div style={{
              borderTop: `1px solid ${T.line}`, paddingTop: 14, marginTop: 8,
              display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12,
            }}>
              <Field label="Your name" testid="maison-input-name">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  style={INPUT_STYLE}
                />
              </Field>
              <Field label="Email" testid="maison-input-email">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@…"
                  style={INPUT_STYLE}
                />
              </Field>
            </div>
            <Field label="Phone (optional)" testid="maison-input-phone">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98xxxxxxx"
                style={INPUT_STYLE}
              />
            </Field>

            {error && (
              <p data-testid="maison-request-error" style={{ color: "#9a3535", fontSize: 13, margin: "4px 0 10px" }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="maison-request-submit"
              disabled={busy}
              style={{
                background: T.ink, color: T.bg, border: 0,
                borderRadius: 999, padding: "13px 22px",
                fontSize: 14, fontWeight: 600,
                cursor: busy ? "wait" : "pointer",
                width: "100%", fontFamily: "inherit",
                opacity: busy ? 0.6 : 1, marginTop: 8,
              }}
            >
              {busy ? "Sending…" : "Send brief to the Concierge®"}
            </button>
            <p style={{ fontSize: 11.5, color: T.ink3, textAlign: "center", margin: "8px 0 0", fontStyle: "italic" }}>
              The Concierge® replies within the hour with options or a price. This is a request, not a confirmed booking. <a href="/legal" target="_blank" rel="noopener" style={{ color: T.brass, textDecoration: "none", borderBottom: `1px dotted ${T.brass}` }}>Terms</a>.
            </p>
            {category === "wine" && (
              <p
                data-testid="maison-wine-age-note"
                style={{
                  fontSize: 11, color: T.ink3, textAlign: "center",
                  margin: "10px 0 0", fontStyle: "italic", lineHeight: 1.5,
                }}
              >
                Wine, spirits &amp; rare bottles are sourced only on behalf of members of legal drinking age in the delivery jurisdiction.
              </p>
            )}
          </>
        )}
      </form>
    </div>
  );
}


function Field({ label, testid, children }) {
  return (
    <label
      data-testid={testid}
      style={{ display: "block", marginBottom: 12 }}
    >
      <span style={{
        display: "block",
        fontSize: 11, color: T.ink3, letterSpacing: "0.14em",
        textTransform: "uppercase", fontWeight: 600,
        fontFamily: "ui-monospace, monospace",
        marginBottom: 6,
      }}>{label}</span>
      {children}
    </label>
  );
}

const INPUT_STYLE = {
  width: "100%", boxSizing: "border-box",
  background: T.card,
  border: `1px solid ${T.line2}`,
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14, color: T.ink,
  outline: "none",
  fontFamily: "inherit",
};
